import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";

import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  Stack,
  TextField,
} from "@mui/material";
import { AppContext } from "App";
import { USER_ROLE_ADMIN } from "core/useSession";
import debounce from "lodash/debounce";
import uniqBy from "lodash/uniqBy";
import { useFormStatus } from "mui-toolkit/useFormStatus";
import { enqueueSnackbar } from "notistack";

import { UsersApiHelper } from "../../api/ApiHelper";

/**
 * AgentAddEditDialog
 * Props:
 * - open: boolean
 * - addEditAgent: object (existing agent) or null
 * - onChange: function(updatedAgent)
 * - onClose: function()
 */
export default function AgentAddEditDialog({ open, addEditAgent, onChange, onClose }) {
  const { globalState } = useContext(AppContext);
  const [searchText, setSearchText] = useState("");
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [state, setState] = useState({
    first_name: "",
    last_name: "",
    business_name: "",
    email: "",
    member_id: null,
  });
  const { component: formStatusComponent } = useFormStatus();

  const searchMembers = useCallback(async () => {
    try {
      setIsLoading(true);
      const __authTokens = { ...globalState };
      if (globalState.user_role === USER_ROLE_ADMIN) {
        delete __authTokens.agent_token;
      }
      delete __authTokens.producer_token;
      let _membersLastName = UsersApiHelper.selectMembers({ last_name: searchText }, __authTokens);
      let _membersBusinessName = UsersApiHelper.selectMembers({ business_name: searchText }, __authTokens);
      const _members = [...(await _membersLastName), ...(await _membersBusinessName)];
      const _uniqueMembers = uniqBy(_members, (p) => p.id).filter((p) => p.agent_token !== null);
      const searchTokens = searchText
        ?.split(/,| /)
        ?.filter((t) => t)
        ?.map((t) => t?.toLowerCase());
      if (searchTokens?.length) {
        _uniqueMembers.forEach((p) => {
          let searchScore = 0;
          p.first_name?.split(" ")?.forEach((token) => {
            searchScore += searchTokens.filter((t) => t === token?.toLowerCase())?.length;
          });
          p.last_name?.split(" ")?.forEach((token) => {
            searchScore += searchTokens.filter((t) => t === token?.toLowerCase())?.length;
          });
          p.business_name?.split(" ")?.forEach((token) => {
            searchScore += searchTokens.filter((t) => t === token?.toLowerCase())?.length;
          });

          p.searchScore = searchScore;
        });
      }
      const _uniqueMembersSorted = _uniqueMembers.sort((a, b) => (b?.searchScore || 0) - (a?.searchScore || 0));

      setMembers(_uniqueMembersSorted);
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      setMembers([]);
      enqueueSnackbar("Error fetching members", { variant: "error" });
    }
  }, [globalState, searchText]);

  const debouncedSearch = useMemo(() => debounce(searchMembers, 300), [searchMembers]);

  useEffect(() => {
    if (searchText.trim()) {
      debouncedSearch();
    } else {
      setMembers([]);
    }
    return () => {
      debouncedSearch.cancel();
    };
  }, [searchText, debouncedSearch]);

  useEffect(() => {
    if (addEditAgent && addEditAgent.id) {
      setState({ ...addEditAgent });
    } else {
      setState({ first_name: "", last_name: "", business_name: "", email: "" });
    }
    setSelected(null);
    setSearchText("");
  }, [addEditAgent]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setState((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const hasBusinessName = state.business_name?.trim();
    if (!hasBusinessName && (!state.first_name.trim() || !state.last_name.trim())) {
      enqueueSnackbar("First and Last Name are required if Business Name is not provided.", { variant: "error" });
      return false;
    }
    if (!state.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email)) {
      enqueueSnackbar("Valid Email is required.", { variant: "error" });
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    onChange?.(state);
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          onClose();
        }
      }}
    >
      <DialogTitle>Add Agent</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ pt: 1 }}>
          <Grid item xs={12}>
            <Autocomplete
              value={selected}
              onChange={(_, newVal) => {
                setSelected(newVal);
                if (newVal) {
                  setState({
                    first_name: newVal.first_name || newVal.First_name || "",
                    last_name: newVal.last_name || newVal.Last_name || "",
                    business_name: newVal.business_name || newVal.Business_name || "",
                    email: newVal.email || newVal.Email || "",
                    member_id: newVal.id ?? newVal.Id,
                  });
                }
              }}
              inputValue={searchText}
              onInputChange={(_, newInput) => setSearchText(newInput)}
              options={members}
              filterOptions={(options) => options}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(opt) => {
                if (!opt) return "";
                const fullName = `${opt.first_name ?? ""} ${opt.last_name ?? ""}`.trim();
                return fullName || opt.business_name || "";
              }}
              renderOption={(props, option) => {
                const fullName = `${option.first_name || ""} ${option.last_name || ""}`.trim();
                const label = fullName || option.business_name;
                if (!label) return null;
                return (
                  <li {...props} key={option.id}>
                    {label}
                  </li>
                );
              }}
              loading={isLoading}
              openOnFocus
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Members"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isLoading ? (
                          <CircularProgress style={{ position: "relative", right: "20px" }} color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          {selected && (
            <>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <TextField
                    label="First Name"
                    name="first_name"
                    value={state.first_name}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    disabled={!!selected}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <TextField
                    label="Last Name"
                    name="last_name"
                    value={state.last_name}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    disabled={!!selected}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <TextField
                    label="Business Name"
                    name="business_name"
                    value={state.business_name}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ readOnly: true }}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <TextField
                    label="Email"
                    name="email"
                    type="email"
                    value={state.email}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{ readOnly: true }}
                  />
                </FormControl>
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Stack direction="row-reverse" spacing={1} useFlexGap flexWrap="wrap" justifyContent="flex-end">
          <Button variant="contained" onClick={handleSubmit}>
            {addEditAgent?.id ? "Save" : "Add"}
          </Button>
          <Button variant="outlined" color="secondary" onClick={onClose}>
            Cancel
          </Button>
        </Stack>
      </DialogActions>
      {formStatusComponent}
    </Dialog>
  );
}
